import { PapiDeviation } from '@wix/da-papi-types';
import {
  getAllowOffers,
  getHasBuyItNow,
  getHasOffers,
  getHasOwnOffer,
  getHasRejectedOffer,
  getIsBeforeOfferEndTime,
  getIsPending,
} from '../../../utils/adoptables';
import type { ElementGroup } from '../types';

/**
 * An element group shown to an adoptable's prospective buyers.
 */
export interface BaseAdoptableBuyerElementGroup<
  ElementPlacementType,
  ElementGroupPlacementsType extends ElementPlacementType,
> extends ElementGroup<ElementPlacementType> {
  groupPlacement: ElementGroupPlacementsType;
  buyItNow?: boolean;
  makeOffer?: boolean;
  manageOffer?: boolean;
  viewOffers?: boolean;
}

export function getAdoptableBuyerElementGroupBase<
  ElementPlacementType,
  ElementGroupPlacementsType extends ElementPlacementType,
>(deviation: PapiDeviation, isMobile: boolean, isOwner: boolean) {
  if (!deviation.extended?.adoptable?.canAdopt || isOwner || isMobile) {
    return false;
  }

  const allowOffers = getAllowOffers(deviation);
  const isBeforeOfferEndTime = getIsBeforeOfferEndTime(deviation);
  const hasOwnOffer = getHasOwnOffer(deviation);
  const canMakeNewOffer = allowOffers && isBeforeOfferEndTime && !hasOwnOffer;

  const hasRejectedOffer = getHasRejectedOffer(deviation);
  const canManageExistingOffer =
    allowOffers && isBeforeOfferEndTime && hasOwnOffer && !hasRejectedOffer;
  const hasBuyItNow = getHasBuyItNow(deviation);
  const isPending = getIsPending(deviation);
  const hasViewOffers = getHasOffers(deviation) && isPending;

  const group: Required<
    Omit<
      BaseAdoptableBuyerElementGroup<ElementPlacementType, ElementGroupPlacementsType>,
      'countOnBar' | 'groupPlacement'
    >
  > = {
    buyItNow: hasBuyItNow,
    makeOffer: canMakeNewOffer,
    manageOffer: canManageExistingOffer,
    viewOffers: hasViewOffers,
  };

  return group;
}
